'use client';

import { Elements } from '@kontent-ai/delivery-sdk';
import { handleAdditionalMedicalSubmit } from 'actions/Forms';
import {
  type AdditionalMedicalPlanForm,
  InputChoice,
} from 'kontent-ai/content-types';
import { usePlayAudio } from 'hooks/usePlayAudio';
import { parseAudio } from 'lib/content-parsers/componentParsers';
import { useRouter } from 'next/navigation';
import { useActionState, useEffect, useMemo, useState } from 'react';
import { ComponentFields } from 'types/content';

const formatChoices = (
  choices: Elements.LinkedItemsElement<InputChoice>
): string[] => {
  return choices.linkedItems?.map((choice) => {
    return choice.elements.label.value;
  });
};

const AdditionalMedicalPlanForm = ({
  data,
  onContinue,
  setCurrentAudioCaptions,
  setCurrentAudioSource,
  setCurrentAudioType,
}: ComponentFields) => {
  const router = useRouter();
  const playAudio = usePlayAudio({
    setCurrentAudioSource,
    setCurrentAudioType,
    setCurrentAudioCaptions,
  });

  // audioyes isn't used yet, when a selection is made the spto route is forced instead
  const parsedAudio = useMemo(() => {
    const elements = (data as AdditionalMedicalPlanForm).elements;
    return {
      no: parseAudio(elements.audiono?.linkedItems[0]),
      'not-sure': parseAudio(elements.audionotsure?.linkedItems[0]),
    };
  }, [data]);

  const [state, formAction, isPending] = useActionState(
    handleAdditionalMedicalSubmit,
    {
      data: new FormData(),
      errors: {},
      results: {
        checkboxes: [],
        radio: null,
      },
      success: false,
    }
  );

  useEffect(() => {
    if (state.success) {
      /* Save the Data and lets move on.. */
      // eslint-disable-next-line no-console
      console.log({
        msg: 'Saving Additional Medical Plans',
        data: state.results,
      });

      // We don't support any of those plan types in the checkboxes
      // so lets take them to the LIG out page
      if (state.results.checkboxes.length > 0) {
        router.push('/spto');
      } else {
        const responseAudio =
          parsedAudio[state.results.radio as keyof typeof parsedAudio];

        if (!playAudio(responseAudio)) {
          onContinue();
        }
      }
    }
  }, [state.results, state.success]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
  const [selectedRadio, setSelectedRadio] = useState<string | null>(null);
  const hasMadeSelection = selectedCheckboxes.length > 0 || selectedRadio;

  // Selecting checkbox(es) will uncheck radio button
  const handleCheckboxSelection = (program: string) => {
    setSelectedRadio(null);

    setSelectedCheckboxes((prev) =>
      prev.includes(program)
        ? prev.filter((p) => p !== program)
        : [...prev, program]
    );
  };

  // Selecting radio button will uncheck checkbox(es)
  const handleRadioSelection = (value: string) => {
    setSelectedRadio(value);
    setSelectedCheckboxes([]);
  };

  const title = data.elements.title.value;
  const subtitle = data.elements.subtitle.value;
  const choices = formatChoices(
    data.elements.choices as Elements.LinkedItemsElement<InputChoice>
  );
  const RADIO_DATA = [
    {
      value: 'no',
      label: "No, I don't",
      testId: 'radio-additional-medical-no',
    },
    {
      value: 'not-sure',
      label: "I'm not sure",
      testId: 'radio-additional-medical-not-sure',
    },
  ];

  return (
    <section className="form-container">
      <form action={formAction}>
        <fieldset className="has-divider">
          <div className="form-header">
            <legend>{title}</legend>
          </div>
          <div className="row">
            <p className="explanation-text">{subtitle}</p>
            <div className="two-column">
              {choices.map((choice, idx) => {
                return (
                  <label key={idx} className="checkbox-button">
                    <input
                      type="checkbox"
                      name="additional"
                      value={choice}
                      checked={selectedCheckboxes.includes(choice)}
                      onChange={() => handleCheckboxSelection(choice)}
                    />
                    <span className="checkbox"></span>
                    <span>{choice}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </fieldset>
        <fieldset>
          <div className="row">
            <p className="explanation-text">Or</p>
            <div className="radio-container">
              {RADIO_DATA.map((option, idx) => {
                return (
                  <label key={idx} className="radio-button">
                    <input
                      type="radio"
                      name="response"
                      value={option.value}
                      onChange={() => handleRadioSelection(option.value)}
                      checked={selectedRadio === option.value}
                    />
                    <span className="radio" data-testid={option.testId}></span>
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </fieldset>
        <button
          className="button primary submit"
          type="submit"
          data-testid="submit-additional-medical"
          disabled={!hasMadeSelection}
        >
          Submit
        </button>
        {isPending && <div className="spinner"></div>}
      </form>
    </section>
  );
};

export default AdditionalMedicalPlanForm;
